import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Plus } from 'lucide-react';
import api from '../../services/api';
import useChiudiConEsc from './useChiudiConEsc';
import SedutaPacchetto from './SedutaPacchetto';
import { giornoValido } from './giorno';
import {
    ORA_INIZIO, ORA_FINE, dueCifre, durataMinuti, nomeAppuntamento, statoDi,
} from './griglia';
import useRicercaPrestazioni from '../../hooks/useRicercaPrestazioni';
import SegnoMancate, { PallinoMancate } from '../SegnoMancate';
import { oggiClinica, oraIta, giornoIta, dataOraIta, istanteItaliano, perCampoItaliano } from '../../utils/oraItaliana';
import { useSporco, useUscitaProtetta } from '../../utils/uscitaProtetta';
import { notificaErrore, notificaAvviso, messaggioDa } from '../../utils/notifiche';
import { templateAPI } from '../../services/templateApi';
import SceltaFirma from '../pazienti/SceltaFirma';
import FirmaConsenso from '../pazienti/FirmaConsenso';
import '../../styles/AgendaPannello.css';

/**
 * PannelliAgenda — il pannello a destra dell'Agenda: il dettaglio di un
 * appuntamento e il nuovo appuntamento (stile «Console», fase 2; mockup
 * «Agenda», Alex 6/10/2026; docs/proposte/26-stile-console.md).
 *
 * Con lo stile nuovo il dettaglio e il nuovo appuntamento non sono piu'
 * finestre sopra la pagina ma un pannello accanto alla griglia: la griglia
 * resta a video e un clic su un altro appuntamento cambia il pannello. Con lo
 * stile di prima restano le finestre di Agenda.jsx, identiche (interruttore
 * dello stile, 14/10/2026).
 *
 * UN FILE A PARTE e non dentro Agenda.jsx: la griglia dell'Agenda si
 * ridisegna nella fase 3, e il pannello deve poter restare com'e'. Le regole
 * sono tutte quelle delle rotte di prima (abilitazione, sovrapposizioni,
 * consensi, giorno della clinica, sala d'attesa, legame alla visita aperta):
 * qui cambia dove si vedono, non che cosa decidono. Il pannello non decide
 * cosa conta come arrivo: segna «Arrivato» o «Non presentato» con la
 * rotta di sempre e mostra quello che il server dice.
 */

/* Le ore che il pannello del nuovo appuntamento offre senza un clic sulla
   griglia: le stesse della griglia, un quarto d'ora alla volta (07:00 -
   22:45). Una tendina e non un campo libero: un orario fuori dalla griglia
   non si vedrebbe, e il server lo arrotonderebbe al quarto d'ora lo stesso. */
const ORARI = [];
for (let h = ORA_INIZIO; h <= ORA_FINE; h += 1) {
    for (const m of [0, 15, 30, 45]) ORARI.push(`${dueCifre(h)}:${dueCifre(m)}`);
}

const oggiIso = () => oggiClinica();

/** «ven 9 ott, 10:30»: il giorno e l'ora di un clic sulla griglia, in testa al pannello. */
const giornoEOra = (locale) => {
    const g = new Date(`${locale.slice(0, 10)}T12:00:00Z`).toLocaleDateString('it-IT', {
        timeZone: 'Europe/Rome', weekday: 'short', day: 'numeric', month: 'short',
    });
    return `${g}, ${locale.slice(11, 16)}`;
};

const PannelloLaterale = ({ etichetta, testa, onChiudi, fuoco = false, escAttivo = true, children, ...attributi }) => {
    const io = useRef(null);
    useEffect(() => { if (fuoco && io.current) io.current.focus(); }, [fuoco]);
    useChiudiConEsc(escAttivo, io, onChiudi);
    return (
    <aside className="ag-pannello" aria-label={etichetta} {...attributi} ref={io} tabIndex={-1}>
        <div className="ag-pannello-testa">
            {testa}
            <button type="button" className="ag-pannello-chiudi" onClick={onChiudi} aria-label="Chiudi il pannello">
                <X size={18} aria-hidden="true" />
            </button>
        </div>
        <div className="ag-pannello-corpo">{children}</div>
    </aside>
    );
};

/** Il pannello dichiara all'Agenda come chiedere prima di perdere il lavoro. */
function useRegistraChiedi(rifChiedi, chiedi) {
    useEffect(() => {
        rifChiedi.current = chiedi;
        return () => { if (rifChiedi.current === chiedi) rifChiedi.current = null; };
    });
}

/* Il pannello con una firma in corso non si chiude e non si cambia (vedi
   PannelloAppuntamento, chiediConFirma). */
const MSG_FIRMA_IN_CORSO = "C'è una firma in corso: finiscila o interrompila prima di lasciare questo appuntamento.";

/* L'etichetta in testa al pannello: a che punto e' l'appuntamento, con le
   classi di stato del tema. Stessa precedenza dei pallini della griglia
   (griglia.js, statoDi). */
const STATI = {
    annullato: { testo: 'Annullato', classe: 'rm-stato-errore' },
    blocco: { testo: 'Blocco', classe: 'rm-stato-accento' },
    uscito: { testo: 'Uscito', classe: 'rm-stato-conferma' },
    in_visita: { testo: 'In visita', classe: 'rm-stato-accento' },
    arrivato: { testo: 'Arrivato', classe: 'rm-stato-attenzione' },
    non_presentato: { testo: 'Non presentato', classe: 'rm-stato-errore' },
    prenotato: { testo: 'Prenotato', classe: 'rm-stato-accento' },
};

/* ============================================================
   Il dettaglio di un appuntamento
   ============================================================ */
const PannelloAppuntamento = ({ iniziale, fuoco, versione, colore, nomeAgenda, rifChiedi, onChiudi, onAggiornato }) => {
    const navigate = useNavigate();
    const id = iniziale.id;
    /* Si disegna subito con la riga della griglia, e si completa con la
       rotta del dettaglio: agenda e medico, chi l'ha creato, la seduta del
       pacchetto. Dalla settimana la riga non ha nemmeno le prestazioni. */
    const [x, setX] = useState(iniziale);
    const [letto, setLetto] = useState(false);
    const [erroreLettura, setErroreLettura] = useState(null);
    const [inCorso, setInCorso] = useState(false);
    /* Consenso obbligatorio (22 settembre 2026, migration 201): se una
       prestazione dell'appuntamento lo chiede e il paziente non l'ha
       firmato, «Trasforma in visita» verrebbe rifiutato. Il pannello lo
       dice appena si apre (la stessa rotta che «Trasforma» chiede prima di
       trasformare, templateController.verificaConsensi: le stesse funzioni
       del blocco, consensi_richiesti), e da li' si fa firmare.

       SI FIRMA SOLO DENTRO LA TRASFORMAZIONE (revisione del 6/10/2026). La
       prima versione del pannello faceva firmare da «Firma sul tablet →»
       senza trasformare, e teneva gli id dei documenti nello stato del
       pannello: «Trasforma in visita» premuto da un altro pannello, dopo una
       ricarica o dalla postazione del medico non li mandava, e i documenti
       restavano senza visita per sempre (legaConsensiAllaVisita lega solo
       gli id che riceve). Un consenso senza visita non prende il segno
       «visita annullata» quando quella visita va nel cestino
       (consenso_visita_annullata, migration 292, risposta 5 di Alex del
       3/10) e consenso_valido() continua a contarlo; e non compare fra i
       documenti della visita. Quindi, come prima del pannello: si firma e
       si trasforma nello stesso gesto, e il collegamento e' immediato.
       Firmare all'arrivo senza trasformare vorrebbe il server che lega la
       firma all'appuntamento: e' una decisione di Alex, non di questa
       schermata. */
    const [consensi, setConsensi] = useState(null);
    /* I consensi firmati da questo pannello: «Trasforma in visita» li lega
       alla visita che nasce. Restano qui se la trasformazione e' rifiutata
       (un 409, la rete), cosi' il secondo tentativo dallo stesso pannello li
       manda ancora. */
    const [firmati, setFirmati] = useState([]);
    const [firma, setFirma] = useState(null);   // { consensi, medicoId } mentre si firma
    const [dovesiFirma, setDoveSiFirma] = useState(null);
    const [modifica, setModifica] = useState(false);
    const [form, setForm] = useState(null);
    const [elenchi, setElenchi] = useState({ agende: [], stanze: [] });
    const [annullo, setAnnullo] = useState(null);   // { motivo } mentre si annulla
    /* Il confronto e' col modulo com'era all'apertura della modifica o
       dell'annullamento (versione sale li'); X, Esc, «Annulla» e un clic
       su un altro appuntamento passano da chiedi(). */
    const [versioneForm, setVersioneForm] = useState(0);
    const sporco = useSporco(modifica ? form : annullo, versioneForm);
    const chiedi = useUscitaProtetta(sporco);
    /* Una firma in corso tiene aperto il pannello: la scelta del tablet,
       l'attesa (anche ridotta a icona, quando il resto dell'agenda torna
       cliccabile) o la firma sul computer. Chiuderlo o cambiarlo la
       smonterebbe a meta', e i documenti firmati non arriverebbero piu' alla
       trasformazione che li lega alla visita. */
    const chiediConFirma = () => {
        if (firma) { notificaAvviso(MSG_FIRMA_IN_CORSO); return false; }
        return chiedi();
    };
    /* E fuori dall'Agenda: la firma in corso si dichiara come lavoro a meta'
       (useUscitaProtetta, come le firme di ModulisticaWizard e di
       CompletamentoConsensi), cosi' il menu e «Indietro» chiedono prima, e la
       pagina non si ricarica da sola per una versione nuova mentre il
       paziente firma sul tablet (utils/versioneInLinea.js): la scelta del
       tablet e l'attesa non sono una finestra con role="dialog", e la rete
       dei campi trattenuti non le vede. */
    useUscitaProtetta(!!firma);
    useRegistraChiedi(rifChiedi, chiediConFirma);
    const chiudi = () => { if (chiediConFirma()) onChiudi(); };
    const annullaModifica = () => { if (chiedi()) setModifica(false); };
    const {
        ricerca: ricercaPrest, setRicerca: setRicercaPrest,
        risultati: catalogo, tutte: tuttePrest, setTutte: setTuttePrest,
        pulisci: pulisciRicerca,
    } = useRicercaPrestazioni({
        agendaId: form?.agenda_id || x.agenda_id,
        attiva: !!modifica,
    });

    useEffect(() => {
        let vivo = true;
        api.get(`/agenda/${id}/dettaglio`)
            .then((r) => {
                if (!vivo) return;
                setX(r.data.data.appuntamento);
                setLetto(true);
                setErroreLettura(null);
            })
            .catch((e) => { if (vivo) setErroreLettura(messaggioDa(e, 'Non riesco a leggere l’appuntamento')); });
        return () => { vivo = false; };
    }, [id, versione]);

    const trasformabile = x.tipo === 'visita' && !!x.paziente_id && !x.visita_id && x.stato !== 'annullato';
    useEffect(() => {
        if (!letto || !trasformabile) { setConsensi(null); return undefined; }
        let vivo = true;
        templateAPI.verificaConsensi({ appuntamento_id: id })
            .then((r) => { if (vivo) setConsensi(r.data.data); })
            .catch(() => { if (vivo) setConsensi(null); });
        return () => { vivo = false; };
    }, [letto, trasformabile, id, versione, firmati.length]);

    /* `datetime-local` vuole l'orario da orologio a muro, senza fuso:
       quello della CLINICA, non quello del browser. Prima si componeva
       con getFullYear/getHours, cioe' col fuso della postazione. */
    const apriModifica = async () => {
        setAnnullo(null);
        setForm({
            inizio: perCampoItaliano(x.inizio),
            durata: durataMinuti(x),
            agenda_id: x.agenda_id,
            stanza_id: x.stanza_id || '',
            note: x.note || '',
            prestazioni: (x.prestazioni || []).map((p) => ({
                id: p.prestazione_id || null,
                nome: p.descrizione,
                durata_minuti: p.durata,
                prezzo: p.prezzo,
            })),
        });
        setModifica(true);
        setVersioneForm((v) => v + 1);
        try {
            const [a, s] = await Promise.all([
                api.get('/agenda/agende'),
                api.get('/agenda/stanze'),
            ]);
            setElenchi({
                agende: a.data.data.agende || [],
                stanze: s.data.data.stanze || [],
            });
        } catch (e) {
            setElenchi({ agende: [], stanze: [] });
        }
    };

    /* `azione` esiste perche' segnare «Arrivato» sembrava non fare niente:
       la presenza veniva salvata e davanti restava lo stesso pulsante. Nel
       pannello si vede che e' andata: si rilegge, e in testa compare
       «Arrivato» con l'ora. Prima la finestra si chiudeva. */
    const azione = async (fn) => {
        try {
            setInCorso(true);
            await fn();
            /* `onAggiornato` rilegge la griglia di CHI ha aperto il pannello
               e lo annuncia: un'altra scheda aperta sulla stessa agenda - la
               reception ne tiene due - resterebbe indietro. */
            onAggiornato();
            return true;
        } catch (e) {
            notificaErrore(messaggioDa(e, 'Non riesco ad aggiornare l’appuntamento'));
            return false;
        } finally { setInCorso(false); }
    };

    const salvaModifica = async () => {
        const ok = await azione(async () => {
            /* L'orario nel campo e' ora italiana: l'istante lo ricava
               `istanteItaliano`, non `new Date` col fuso del browser. */
            const inizio = istanteItaliano(form.inizio);
            /* Se ci sono prestazioni la durata la dettano loro, come in
               creazione: il campo a mano vale solo senza prestazioni. */
            const minuti = (form.prestazioni || []).length
                ? form.prestazioni.reduce((s, p) => s + (p.durata_minuti || 30), 0)
                : (parseInt(form.durata, 10) || 30);
            const fine = new Date(inizio.getTime() + minuti * 60000);
            const r = await api.put(`/agenda/${x.id}`, {
                inizio: inizio.toISOString(),
                fine: fine.toISOString(),
                agenda_id: parseInt(form.agenda_id, 10),
                stanza_id: form.stanza_id === '' ? null : parseInt(form.stanza_id, 10),
                note: form.note,
                prestazioni: (form.prestazioni || []).map((p) => ({
                    prestazione_id: p.id,
                    descrizione: p.nome,
                    durata_minuti: p.durata_minuti,
                    prezzo: p.prezzo,
                })),
            });
            const c = r.data.data?.conflitti;
            if (c && (c.stanza?.length || c.agenda?.length)) notificaAvviso(r.data.message);
        });
        if (ok) { setModifica(false); setVersioneForm((v) => v + 1); }
    };

    const confermaAnnullo = async () => {
        const motivo = annullo?.motivo || '';
        const ok = await azione(() => api.delete(`/agenda/${x.id}`, { data: { motivo } }));
        if (ok) { setAnnullo(null); setVersioneForm((v) => v + 1); }
    };

    /* Dopo la trasformazione serve il posto dove si lavora sulla visita,
       cioe' le procedure mediche: aprire la home della scheda paziente
       obbligherebbe a un altro clic per arrivarci. */
    const vaiAlleProcedure = (pazienteId) =>
        navigate(`/pazienti/${pazienteId}`, { state: { schermata: 'procedure' } });

    /* Con una visita del medico ancora aperta l'appuntamento si lega a
       quella e le prestazioni prenotate non si copiano (Alex, 3/10/2026:
       le registra il medico). Lo dice il server, e qui si mostra: chi
       trasforma deve sapere che nella visita non le trovera'. */
    const trasforma = async (documenti = []) => {
        const r = await api.post(`/agenda/${x.id}/trasforma-in-visita`,
                                 { consensi_documenti: documenti });
        if (r.data?.data?.legata) notificaAvviso(r.data.message);
        const pid = r.data?.data?.paziente_id;
        /* La voce di un pacchetto aperto non entra nella visita (Alex,
           3/10/2026): il server lo dice nel messaggio, «Pacchetto attivo:
           seduta N di M — ...», e qui si mostra. La regola sta nel database
           (pacchetti_aperti), non qui. */
        if (r.data?.data?.pacchetti?.length) notificaAvviso(r.data.message);
        if (pid) vaiAlleProcedure(pid);
    };

    /* I modelli da firmare, con la prestazione che li chiede accanto. */
    const daFirmare = (v) => v.modelli.map((t) => {
        const chi = v.prestazioni.find((p) => p.mancanti.includes(t.id));
        const riga = chi && (x.prestazioni || []).find(
            (p) => Number(p.prestazione_id) === Number(chi.prestazione_id));
        return { ...t, _prestazione: riga ? riga.descrizione : null };
    });

    /* Prima di trasformare si chiede al server che cosa manca: paziente,
       prestazioni ed esecutore li ricava lui dall'appuntamento, con le
       stesse regole della trasformazione. Non passa da azione(): la firma
       vive qui dentro, e la griglia ricaricata a meta' non deve toccarla. */
    const trasformaInVisita = async () => {
        let v;
        try {
            setInCorso(true);
            v = (await templateAPI.verificaConsensi({ appuntamento_id: x.id })).data.data;
        } catch (e) {
            notificaErrore(messaggioDa(e, 'Non riesco a verificare i consensi dell’appuntamento'));
            return;
        } finally { setInCorso(false); }
        const senza = v.prestazioni.find((p) => p.senza_modello);
        if (senza) { notificaErrore(senza.messaggio); return; }
        if (v.modelli.length) {
            setDoveSiFirma(null);
            setFirma({ medicoId: v.medico_id, consensi: daFirmare(v) });
            return;
        }
        azione(() => trasforma(firmati));
    };

    /* Firmati tutti: la trasformazione parte subito e li lega alla visita
       (vedi in testa: si firma solo dentro la trasformazione). «Firma sul
       tablet e trasforma →» del riquadro del consenso e «Trasforma in
       visita» sono lo stesso gesto, trasformaInVisita: la firma passa dalla
       scelta di sempre (SceltaFirma: il tablet, o questo computer se non ce
       n'e'). */
    const firmatiOra = (documenti) => {
        setFirma(null);
        setDoveSiFirma(null);
        const ids = (documenti || []).map((d) => d?.id ?? d?.documento_id).filter(Boolean);
        const tutti = [...firmati, ...ids];
        setFirmati(tutti);
        azione(() => trasforma(tutti));
    };

    const stato = statoDi(x);
    const etichettaStato = STATI[stato] || STATI.prenotato;
    const nome = nomeAppuntamento(x);
    const annullato = x.stato === 'annullato';
    const conPaziente = x.tipo === 'visita';
    const mancanti = consensi && !consensi.visita_aperta ? (consensi.modelli || []) : [];
    const senzaModello = consensi ? (consensi.prestazioni || []).find((p) => p.senza_modello) : null;
    const coloreAgenda = x.agenda_colore || colore;
    const telefono = x.paz_telefono || x.paziente_telefono;

    return (
        <>
            {/* Le firme restano finestre: stanno sopra la pagina, e mentre
                si firma il pannello resta dov'e'. */}
            {firma && dovesiFirma === null && (
                <SceltaFirma
                    pazienteId={x.paziente_id}
                    visitaId={null}
                    medicoId={firma.medicoId}
                    consensi={firma.consensi}
                    onSuPc={() => setDoveSiFirma('pc')}
                    onCompletato={firmatiOra}
                    onAnnulla={() => { setFirma(null); setDoveSiFirma(null); }}
                />
            )}
            {firma && dovesiFirma === 'pc' && (
                <FirmaConsenso
                    pazienteId={x.paziente_id}
                    visitaId={null}
                    medicoId={firma.medicoId}
                    consensi={firma.consensi}
                    obbligatori
                    onClose={() => { setFirma(null); setDoveSiFirma(null); }}
                    onComplete={firmatiOra}
                />
            )}
            <PannelloLaterale
                etichetta="Appuntamento selezionato"
                fuoco={fuoco}
                escAttivo={!firma}
                data-pannello-appuntamento={x.id}
                onChiudi={chiudi}
                testa={(
                    <>
                        <span className={`rm-stato ${etichettaStato.classe} ag-pannello-stato`} data-stato={stato}>
                            {etichettaStato.testo}
                            {stato === 'arrivato' && x.presenza_il ? ` ${oraIta(x.presenza_il)}` : ''}
                        </span>
                        <span className="ag-pannello-numero rm-numeri" title="Numero dell'appuntamento">#{x.id}</span>
                    </>
                )}
            >
                <div className="ag-pannello-sezione ag-pannello-chi">
                    {x.paziente_id ? (
                        <button type="button" className="ag-pannello-nome" onClick={() => navigate(`/pazienti/${x.paziente_id}`)}
                                title="Apri la scheda del paziente">
                            {nome}
                        </button>
                    ) : (
                        <span className="ag-pannello-nome">{nome}</span>
                    )}
                    <span className="ag-pannello-quando rm-numeri">
                        {oraIta(x.inizio)} – {oraIta(x.fine)} · {durataMinuti(x)} min
                        {giornoIta(x.inizio) !== oggiIso() ? ` · ${new Date(x.inizio).toLocaleDateString('it-IT', { timeZone: 'Europe/Rome', weekday: 'short', day: 'numeric', month: 'short' })}` : ''}
                    </span>
                    {conPaziente && !x.paziente_id && (
                        <span className="ag-nota">Non ancora in archivio: l&apos;anagrafica si completa all&apos;arrivo.</span>
                    )}
                </div>

                {erroreLettura && <div className="ag-errore">{erroreLettura}</div>}

                <dl className="ag-pannello-dati ag-pannello-sezione">
                    <dt>Agenda</dt>
                    <dd>{x.agenda_nome || nomeAgenda || '—'}</dd>
                    {x.medico && (
                        <>
                            <dt>Medico</dt>
                            <dd className="ag-pannello-medico">
                                <i className="ag-colonna-colore" aria-hidden="true"
                                   style={coloreAgenda ? { '--ag-colore': coloreAgenda } : undefined} />
                                {x.medico}
                            </dd>
                        </>
                    )}
                    {(x.prestazioni || []).length > 0 && (
                        <>
                            <dt>{x.prestazioni.length === 1 ? 'Prestazione' : 'Prestazioni'}</dt>
                            <dd className="ag-prestazioni">
                                {x.prestazioni.map((p, i) => (
                                    <span key={i}>
                                        {p.descrizione}
                                        <em className="rm-numeri">{p.durata} min{p.prezzo ? ` · ${p.prezzo} €` : ''}</em>
                                    </span>
                                ))}
                            </dd>
                        </>
                    )}
                    {x.stanza_nome && (
                        <>
                            <dt>Stanza</dt>
                            <dd className="ag-pannello-medico">
                                <i className="ag-colonna-colore" aria-hidden="true"
                                   style={x.stanza_colore ? { '--ag-colore': x.stanza_colore } : undefined} />
                                {x.stanza_nome}
                            </dd>
                        </>
                    )}
                    {telefono && (<><dt>Telefono</dt><dd className="rm-numeri">{telefono}</dd></>)}
                    {x.id_paziente && (<><dt>Codice</dt><dd className="rm-numeri">{x.id_paziente}</dd></>)}
                    {x.note && !modifica && (<><dt>Note</dt><dd className="ag-note">{x.note}</dd></>)}
                    {annullato && x.motivo_cancellazione && (<><dt>Motivo</dt><dd className="ag-note">{x.motivo_cancellazione}</dd></>)}
                </dl>

                {/* La seduta di un pacchetto aperto (Alex, 6/10/2026, domanda 11):
                    l'avviso di «Trasforma in visita» anche qui, e per
                    l'amministrazione, su un giorno passato, «Continua
                    pacchetto». Lo decide il server: e' lo stesso componente
                    del riquadro di prima, una copia sola. */}
                {!modifica && <SedutaPacchetto appuntamento={x} />}

                {modifica && form ? (
                    <div className="ag-modifica ag-pannello-sezione">
                        <div className="ag-mod-doppio">
                            <label className="ag-campo">
                                <span>Inizio</span>
                                <input
                                    type="datetime-local"
                                    step="900"
                                    value={form.inizio}
                                    onChange={(e) => setForm({ ...form, inizio: e.target.value })}
                                />
                            </label>
                            <label className="ag-campo ag-mod-corto">
                                <span>Durata</span>
                                <input
                                    type="number"
                                    min="5"
                                    step="5"
                                    value={(form.prestazioni || []).length
                                        ? form.prestazioni.reduce((s, p) => s + (p.durata_minuti || 30), 0)
                                        : form.durata}
                                    disabled={(form.prestazioni || []).length > 0}
                                    onChange={(e) => setForm({ ...form, durata: e.target.value })}
                                />
                            </label>
                        </div>

                        <label className="ag-campo">
                            <span>Agenda</span>
                            <select
                                value={form.agenda_id}
                                onChange={(e) => setForm({ ...form, agenda_id: e.target.value })}
                            >
                                {elenchi.agende.map((a) => (
                                    <option key={a.id} value={a.id}>{a.nome}</option>
                                ))}
                            </select>
                        </label>

                        <label className="ag-campo">
                            <span>Stanza</span>
                            <select
                                value={form.stanza_id}
                                onChange={(e) => setForm({ ...form, stanza_id: e.target.value })}
                            >
                                <option value="">nessuna</option>
                                {elenchi.stanze.map((s) => (
                                    <option key={s.id} value={s.id}>{s.nome}</option>
                                ))}
                            </select>
                        </label>

                        <label className="ag-campo">
                            <span>Note</span>
                            <input
                                value={form.note}
                                onChange={(e) => setForm({ ...form, note: e.target.value })}
                            />
                        </label>

                        <div className="ag-mod-prestazioni">
                            <span className="ag-mod-titolo">Prestazioni</span>

                            {(form.prestazioni || []).map((p, i) => (
                                <div key={i} className="ag-prest-scelta">
                                    <span>{p.nome}</span>
                                    <em className="rm-numeri">{p.durata_minuti || 30} min</em>
                                    <button type="button" aria-label="Togli la prestazione" onClick={() => setForm({
                                        ...form,
                                        prestazioni: form.prestazioni.filter((_, j) => j !== i),
                                    })}>
                                        <X size={14} aria-hidden="true" />
                                    </button>
                                </div>
                            ))}

                            <label className="ag-campo">
                                <input
                                    value={ricercaPrest}
                                    onChange={(e) => setRicercaPrest(e.target.value)}
                                    placeholder="aggiungi: scrivi almeno tre lettere…"
                                    aria-label="Aggiungi una prestazione"
                                />
                            </label>

                            <label className="ag-spunta">
                                <input
                                    type="checkbox"
                                    checked={tuttePrest}
                                    onChange={(e) => setTuttePrest(e.target.checked)}
                                />
                                <span>Cerca fra tutte, non solo le sue</span>
                            </label>

                            {catalogo.length > 0 && (
                                <div className="ag-risultati">
                                    {catalogo.map((p) => (
                                        <button type="button" key={p.id} onClick={() => {
                                            setForm({
                                                ...form,
                                                prestazioni: [...(form.prestazioni || []), p],
                                            });
                                            pulisciRicerca();
                                        }}>
                                            <b>{p.nome}</b>
                                            <span className="rm-numeri">
                                                {p.durata_minuti || 30} min
                                                {p.prezzo ? ` · ${p.prezzo} €` : ''}
                                                {p.stanza ? ` · ${p.stanza}` : ''}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="ag-pannello-azioni">
                            <button type="button" onClick={annullaModifica} disabled={inCorso}>
                                Annulla
                            </button>
                            <button type="button" className="ag-conferma" onClick={salvaModifica} disabled={inCorso}>
                                Salva
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="ag-pannello-sezione ag-pannello-fare">
                        {/* Il consenso che manca: si vede prima di accettare,
                            e si fa firmare da qui. */}
                        {trasformabile && mancanti.length > 0 && (
                            <div className="ag-consenso" data-consenso-mancante>
                                <b>
                                    {mancanti.length === 1
                                        ? `Consenso «${mancanti[0].titolo}» mancante`
                                        : `Consensi mancanti: ${mancanti.map((t) => `«${t.titolo}»`).join(', ')}`}
                                </b>
                                <span>Va firmato prima della seduta. Si firma trasformando in visita, e resta legato alla visita.</span>
                                <button type="button" className="ag-consenso-firma" onClick={trasformaInVisita} disabled={inCorso}>
                                    Firma sul tablet e trasforma →
                                </button>
                            </div>
                        )}
                        {trasformabile && senzaModello && (
                            <div className="ag-consenso ag-consenso--senza">{senzaModello.messaggio}</div>
                        )}

                        {/* Trasforma in visita: esplicito, non legato alla
                            presenza, perche' i conteggi vengono dalle prestazioni
                            eseguite e una visita si crea solo quando c'e' davvero.
                            Se l'appuntamento e' gia' una visita, si va li'. */}
                        {conPaziente && x.paziente_id && !annullato && (
                            x.visita_id ? (
                                <button type="button" className="ag-vai-visita"
                                        onClick={() => vaiAlleProcedure(x.paziente_id)}>
                                    Vai alla visita
                                </button>
                            ) : (
                                <button type="button" className="ag-trasforma" disabled={inCorso}
                                        onClick={trasformaInVisita}>
                                    Trasforma in visita
                                </button>
                            )
                        )}

                        {/* «Arrivato» e «Non presentato» si offrono sempre, come nella
                            finestra di prima: cosa conta come arrivo lo decide il
                            server. Lo stato «In visita» in testa (appuntamento
                            «in_corso») e' solo la lettura del dato: oggi nessuna
                            strada lo scrive, e il riquadro «il medico ha gia' preso
                            il paziente» col rifiuto del server sono del ramo
                            arrivo-trasforma, che non e' in main. */}
                        {conPaziente && !annullato && (
                            <div className="ag-presenza">
                                {/* Se «Arrivato» lega l'appuntamento a una visita
                                    del medico che c'e' gia' (ancora aperta), le
                                    prestazioni prenotate non ci entrano: le
                                    aggiunge il medico. Lo dice il server, come per
                                    «Trasforma in visita», e qui si mostra
                                    (revisione del 3/10/2026 pomeriggio). */}
                                <button
                                    type="button"
                                    className={x.presenza === 'presente' ? 'attivo' : ''}
                                    aria-pressed={x.presenza === 'presente'}
                                    disabled={inCorso}
                                    onClick={() => azione(async () => {
                                        const r = await api.post(`/agenda/${x.id}/presenza`, { presenza: 'presente' });
                                        if (r.data?.data?.legata && r.data?.data?.prenotate) notificaAvviso(r.data.message);
                                    })}
                                >
                                    Arrivato
                                </button>
                                <button
                                    type="button"
                                    className={x.presenza === 'assente' ? 'attivo assente' : ''}
                                    aria-pressed={x.presenza === 'assente'}
                                    disabled={inCorso}
                                    onClick={() => azione(() =>
                                        api.post(`/agenda/${x.id}/presenza`, { presenza: 'assente' }))}
                                >
                                    Non presentato
                                </button>
                            </div>
                        )}

                        {!annullato && !annullo && (
                            <div className="ag-pannello-due">
                                <button type="button" onClick={apriModifica} disabled={inCorso}
                                        title="Sposta l'appuntamento o cambiane agenda, stanza, note e prestazioni">
                                    Sposta
                                </button>
                                <button type="button" className="ag-annulla" disabled={inCorso}
                                        onClick={() => { setAnnullo({ motivo: '' }); setVersioneForm((v) => v + 1); }}>
                                    Annulla
                                </button>
                            </div>
                        )}

                        {/* L'annullamento si conferma qui, con il motivo: prima era
                            una domanda del browser. Un appuntamento annullato resta
                            in griglia, barrato, e la fascia torna libera. */}
                        {annullo && (
                            <div className="ag-annullo">
                                <label className="ag-campo">
                                    <span>Motivo dell&apos;annullamento (facoltativo)</span>
                                    <input value={annullo.motivo} autoFocus
                                           onChange={(e) => setAnnullo({ motivo: e.target.value })} />
                                </label>
                                <div className="ag-pannello-azioni">
                                    <button type="button" disabled={inCorso}
                                            onClick={() => { if (chiedi()) setAnnullo(null); }}>
                                        Indietro
                                    </button>
                                    <button type="button" className="ag-annulla ag-annulla--conferma" disabled={inCorso}
                                            onClick={confermaAnnullo}>
                                        Annulla l&apos;appuntamento
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {x.created_at && (
                    <div className="ag-pannello-creato">
                        Creato da {x.creato_da_nome || (x.origine && x.origine !== 'interno' ? 'prenotazione online' : '—')}
                        {' · '}<span className="rm-numeri">{dataOraIta(x.created_at)}</span>
                        {annullato && x.annullato_il && (
                            <> · annullato <span className="rm-numeri">{dataOraIta(x.annullato_il)}</span></>
                        )}
                    </div>
                )}
            </PannelloLaterale>
        </>
    );
};

/* ============================================================
   Nuovo appuntamento
   ============================================================ */
const PannelloNuovo = ({ contesto, agende = [], proposta = null, rifChiedi, onChiudi, onCreato }) => {
    /* Dal clic sulla griglia agenda e ora le dice il clic; dalla barra
       comandi, da «Nuovo» o da «Appuntamento» (contesto.libero) si
       scelgono qui. L'agenda finche' nessuno la sceglie e' la proposta, che
       puo' arrivare dopo l'apertura (gli elenchi dell'agenda): per questo lo
       stato tiene la SCELTA (null = nessuna) e non il valore, e la proposta
       che arriva non fa sembrare il modulo modificato. */
    const libero = !!contesto.libero;
    const [agendaScelta, setAgendaScelta] = useState(null);
    const [giorno, setGiorno] = useState(contesto.giorno || '');
    const [ora, setOra] = useState('');
    const agendaId = libero
        ? Number((agendaScelta === null ? proposta : agendaScelta) || 0) || null
        : contesto.agenda_id;
    const inizioLocale = libero
        ? (giornoValido(giorno) && ora ? `${giorno}T${ora}` : null)
        : contesto.inizio;
    const [tipo, setTipo] = useState('visita');
    const [ricerca, setRicerca] = useState('');
    const [pazienti, setPazienti] = useState([]);
    const [paziente, setPaziente] = useState(contesto.paziente || null);
    const [nomeLibero, setNomeLibero] = useState('');
    const [telefono, setTelefono] = useState(contesto.paziente?.telefono || '');
    const [titolo, setTitolo] = useState('');
    const [prestazioni, setPrestazioni] = useState([]);
    const {
        ricerca: ricercaPrest, setRicerca: setRicercaPrest,
        risultati: catalogo, tutte: tuttePrest, setTutte: setTuttePrest,
        pulisci: pulisciRicerca,
    } = useRicercaPrestazioni({ agendaId: agendaId || undefined });
    const [durata, setDurata] = useState(30);
    const [note, setNote] = useState('');
    const [inCorso, setInCorso] = useState(false);

    /* Chiudere con qualcosa di diverso da com'era chiede conferma: la X,
       Esc, «Annulla» e un clic su un altro posto della griglia passano da
       chiudi() o dalla domanda dell'Agenda. L'istantanea e' quella del
       montaggio (il modulo nasce vuoto). */
    const sporco = useSporco({ tipo, paziente: paziente?.id || null, nomeLibero, telefono, titolo, prestazioni, durata, note,
                               agendaScelta, giorno, ora }, 0);
    const chiedi = useUscitaProtetta(sporco);
    useRegistraChiedi(rifChiedi, chiedi);
    const chiudi = () => { if (chiedi()) onChiudi(); };

    useEffect(() => {
        if (ricerca.trim().length < 3) { setPazienti([]); return undefined; }
        const t = setTimeout(async () => {
            try {
                const r = await api.get('/pazienti', { params: { search: ricerca, limit: 8 } });
                setPazienti(r.data.data.pazienti || []);
            } catch (e) { /* niente */ }
        }, 350);
        return () => clearTimeout(t);
    }, [ricerca]);

    const durataTotale = prestazioni.length
        ? prestazioni.reduce((s, p) => s + (p.durata_minuti || 30), 0)
        : durata;

    const salva = async () => {
        try {
            setInCorso(true);
            const r = await api.post('/agenda', {
                agenda_id: agendaId,
                /* IL CLIC SULLA GRIGLIA (corretto il 23 settembre 2026).
                   Qui partiva `"2026-09-17T10:00"`, un orario SENZA
                   fuso: il server, che gira in UTC, lo leggeva come le
                   10:00 UTC e l'appuntamento compariva alle 12:00.
                   Modifica e trascinamento convertivano gia', quindi
                   nella stessa colonna convivevano due convenzioni.
                   Misurato in produzione: tutti e sette gli
                   appuntamenti creati da una persona erano spostati. */
                inizio: istanteItaliano(inizioLocale).toISOString(),
                tipo,
                paziente_id: paziente?.id || null,
                paziente_nome: paziente ? null : (nomeLibero || null),
                paziente_telefono: telefono || null,
                titolo: tipo === 'blocco' ? titolo : null,
                note: note || null,
                durata_minuti: durataTotale,
                prestazioni: prestazioni.map((p) => ({
                    prestazione_id: p.id,
                    descrizione: p.nome,
                    durata_minuti: p.durata_minuti,
                    prezzo: p.prezzo,
                })),
            });
            if (r.data.message && r.data.message.includes('Attenzione')) {
                notificaAvviso(r.data.message);
            }
            /* Senza il clic sulla griglia, l'agenda deve mostrare dove e'
               finito (Agenda, mostraNuovo). */
            onCreato(libero ? {
                agendaId, giorno: inizioLocale.slice(0, 10), ora: Number(inizioLocale.slice(11, 13)),
            } : null);
        } catch (e) {
            notificaErrore(messaggioDa(e, 'Non riesco a prenotare l’appuntamento'));
        } finally {
            setInCorso(false);
        }
    };

    /* Agenda e orario li chiede anche la rotta («Servono l'agenda e
       l'orario di inizio»): senza, «Prenota» resta spento come senza
       paziente. Tutto il resto lo decide il server. */
    const valido = !!agendaId && !!inizioLocale && (tipo === 'blocco'
        ? titolo.trim().length > 0
        : (paziente || nomeLibero.trim().length > 0));

    return (
        <PannelloLaterale
            etichetta="Nuovo appuntamento"
            data-nuovo-appuntamento={libero ? 'libero' : 'griglia'}
            onChiudi={chiudi}
            testa={(
                <span className="ag-pannello-titolo">
                    <Plus size={16} aria-hidden="true" />
                    {libero ? 'Nuovo appuntamento' : `${contesto.agenda_nome} · ${giornoEOra(contesto.inizio)}`}
                </span>
            )}
        >
            <div className="ag-pannello-sezione">
                {libero && (
                    <>
                        <label className="ag-campo">
                            <span>Agenda</span>
                            <select name="agenda" value={agendaId || ''}
                                    onChange={(e) => setAgendaScelta(e.target.value)}>
                                <option value="">Scegli l&apos;agenda…</option>
                                {agende.map((a) => (
                                    <option key={a.id} value={a.id}>{a.nome}</option>
                                ))}
                            </select>
                        </label>
                        <div className="ag-quando">
                            <label className="ag-campo">
                                <span>Giorno</span>
                                <input type="date" name="giorno" value={giorno}
                                       onChange={(e) => setGiorno(e.target.value)} />
                            </label>
                            <label className="ag-campo">
                                <span>Ora</span>
                                <select name="ora" value={ora} onChange={(e) => setOra(e.target.value)}>
                                    <option value="">Scegli l&apos;ora…</option>
                                    {ORARI.map((o) => <option key={o} value={o}>{o}</option>)}
                                </select>
                            </label>
                        </div>
                    </>
                )}
                <div className="ag-tipi" role="group" aria-label="Che cosa si prenota">
                    <button type="button" className={tipo === 'visita' ? 'attivo' : ''} aria-pressed={tipo === 'visita'}
                            onClick={() => setTipo('visita')}>Appuntamento</button>
                    <button type="button" className={tipo === 'blocco' ? 'attivo' : ''} aria-pressed={tipo === 'blocco'}
                            onClick={() => setTipo('blocco')}>Blocco</button>
                </div>

                {tipo === 'blocco' ? (
                    <label className="ag-campo">
                        <span>Motivo</span>
                        <input value={titolo} onChange={(e) => setTitolo(e.target.value)}
                               placeholder="pranzo, riunione, assenza…" autoFocus />
                    </label>
                ) : (
                    <>
                        {paziente ? (
                            <>
                                <div className="ag-scelto">
                                    <b>{paziente.cognome} {paziente.nome}</b>
                                    <span className="rm-numeri">{paziente.id_paziente}</span>
                                    <button type="button" aria-label="Cambia paziente" onClick={() => setPaziente(null)}>
                                        <X size={14} aria-hidden="true" />
                                    </button>
                                </div>
                                {/* L'avviso sta QUI e non nella scheda paziente:
                                    chi prenota lo deve vedere prima di dare
                                    l'orario, non dopo. Non blocca niente. */}
                                <SegnoMancate paziente={paziente} />
                            </>
                        ) : (
                            <>
                                <label className="ag-campo">
                                    <span>Paziente</span>
                                    <input value={ricerca} autoFocus
                                           onChange={(e) => setRicerca(e.target.value)}
                                           placeholder="cognome, nome o codice fiscale…" />
                                </label>
                                {pazienti.length > 0 && (
                                    <div className="ag-risultati">
                                        {pazienti.map((p) => (
                                            <button type="button" key={p.id} onClick={() => {
                                                setPaziente(p);
                                                setTelefono(p.telefono || '');
                                                setPazienti([]);
                                            }}>
                                                <b>
                                                    {p.cognome} {p.nome}
                                                    <PallinoMancate paziente={p} />
                                                </b>
                                                <span className="rm-numeri">{p.id_paziente}
                                                    {p.data_nascita
                                                        ? ` · ${new Date(p.data_nascita)
                                                            .toLocaleDateString('it-IT')}`
                                                        : ''}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                                <p className="ag-nota">
                                    Se prenota al telefono e non è in archivio, basta
                                    il nome: l'anagrafica si completa all'arrivo.
                                </p>
                                <label className="ag-campo">
                                    <span>Oppure solo nome e telefono</span>
                                    <input value={nomeLibero}
                                           onChange={(e) => setNomeLibero(e.target.value)}
                                           placeholder="Nome e cognome" />
                                </label>
                            </>
                        )}

                        <label className="ag-campo">
                            <span>Telefono</span>
                            <input value={telefono}
                                   onChange={(e) => setTelefono(e.target.value)} />
                        </label>

                        <label className="ag-campo">
                            <span>Prestazione</span>
                            <input value={ricercaPrest}
                                   onChange={(e) => setRicercaPrest(e.target.value)}
                                   placeholder="scrivi almeno tre lettere…" />
                        </label>
                        <label className="ag-spunta">
                            <input type="checkbox" checked={tuttePrest}
                                   onChange={(e) => setTuttePrest(e.target.checked)} />
                            <span>Cerca fra tutte le prestazioni, non solo le sue</span>
                        </label>
                        {catalogo.length > 0 && (
                            <div className="ag-risultati">
                                {catalogo.map((p) => (
                                    <button type="button" key={p.id} onClick={() => {
                                        setPrestazioni([...prestazioni, p]);
                                        pulisciRicerca();
                                    }}>
                                        <b>{p.nome}</b>
                                        <span className="rm-numeri">
                                            {p.durata_minuti || 30} min
                                            {p.prezzo ? ` · ${p.prezzo} €` : ''}
                                            {p.stanza ? ` · ${p.stanza}` : ''}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}

                        {prestazioni.map((p, i) => (
                            <div key={i} className="ag-prest-scelta">
                                <span>{p.nome}</span>
                                <em className="rm-numeri">{p.durata_minuti || 30} min</em>
                                <button type="button" aria-label="Togli la prestazione" onClick={() => setPrestazioni(
                                    prestazioni.filter((_, j) => j !== i))}>
                                    <X size={14} aria-hidden="true" />
                                </button>
                            </div>
                        ))}
                    </>
                )}

                <label className="ag-campo ag-campo--stretto">
                    <span>Durata (minuti)</span>
                    <input type="number" step={15} min={15}
                           value={durataTotale}
                           disabled={prestazioni.length > 0}
                           onChange={(e) => setDurata(parseInt(e.target.value, 10) || 30)} />
                </label>

                <label className="ag-campo">
                    <span>Note</span>
                    <textarea rows={2} value={note}
                              onChange={(e) => setNote(e.target.value)} />
                </label>

                <div className="ag-pannello-azioni">
                    <button type="button" onClick={chiudi}>Annulla</button>
                    <button type="button" className="ag-conferma" disabled={inCorso || !valido}
                            onClick={salva}>
                        Prenota
                    </button>
                </div>
            </div>
        </PannelloLaterale>
    );
};

export { PannelloLaterale, PannelloAppuntamento, PannelloNuovo };
